import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import AdminNav from "../components/adminNav";
import Time from "../components/time";
import Swal from "sweetalert2";
import { useParams, useNavigate, Link } from "react-router-dom";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";

/* ================= helpers ================= */

// returns true only for "0", "1", "2", ... (no sign, no decimals, no spaces)


// format date as d/m/y (no time)
const formatDMY = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const pad2 = (n) => String(n).padStart(2, "0");
const to24h = (h12, ampm) =>
  ampm === "AM" ? Number(h12) % 12 : (Number(h12) % 12) + 12;
const decFromHM = (h24, m) => +(h24 + m / 60).toFixed(2);
const minutesFromDateHM = (dateStr, h24, m) => {
  if (!dateStr) return NaN;
  const y = Number(dateStr.slice(0, 4));
  const mo = Number(dateStr.slice(5, 7));
  const d = Number(dateStr.slice(8, 10));
  const dayOrd = y * 372 + mo * 31 + d; // monotonic ordinal, no TZ math
  return dayOrd * 1440 + h24 * 60 + m;
};
const hhmmPreview = (h24, m) => `${pad2(h24)}:${pad2(m)}`;
const decToHM = (dec) => {
  const n = Number(dec || 0);
  const h = Math.floor(n);
  const m = Math.round((n - h) * 60);
  return [Math.max(0, Math.min(23, h)), Math.max(0, Math.min(59, m))];
};
const h24To12 = (h24) => {
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 || 12;
  return { h12: String(h12), ampm };
};
const ymdLocal = (dt) =>
  `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;

// Clean hour label like "24" or "12.5" (remove trailing .00 / zeros)
const prettyHours = (hours) => {
  const rounded2 = Math.round(hours * 100) / 100; // keep 2-dec precision
  return parseFloat(String(rounded2)).toString();
};

/* HH/HH:MM/H.MM parser helpers (kept if needed elsewhere) */
const hhmmToDecimal = (timeStr) => {
  if (timeStr === null || timeStr === undefined) return NaN;
  const s = String(timeStr).trim();
  if (!s) return NaN;
  let parts;
  if (s.includes(":")) parts = s.split(":");
  else if (s.includes(".")) parts = s.split(".");
  else parts = [s, "0"];
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] ?? "0", 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return NaN;
  if (h < 0 || h > 23 || m < 0 || m > 59) return NaN;
  return h + m / 60;
};
const extractHour = (timeStr) => {
  if (timeStr === null || timeStr === undefined) return NaN;
  const s = String(timeStr).trim();
  if (!s) return NaN;
  let hStr = s;
  if (s.includes(":")) hStr = s.split(":")[0];
  else if (s.includes(".")) hStr = s.split(".")[0];
  const h = parseInt(hStr, 10);
  if (Number.isNaN(h) || h < 0 || h > 23) return NaN;
  return h;
};
const toPeriod = (timeStr) => {
  const h = extractHour(timeStr);
  if (Number.isNaN(h)) return "";
  return h <= 11 ? "AM" : "PM";
};

const Vehicle_position_edit_hire = () => {
  useAuthRedirect();
  const { id, vcode, branch, vehname, vehno, vtype, basis } = useParams();
  const canEdit = hasPermission("Vehicle Daily Progress Entry", "edit");
  const navigate = useNavigate();

  const [locations, setLocations] = useState([]);
  const [hireLocations, setHireLocations] = useState([]);
  const [uom, setUom] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const [formData, setFormData] = useState({
    vehicle_basis: "",
    driver: "",
    sthrs: "",
    endhrs: "",
    stkm: "",
    endkm: "",
    breakhrs: "",
    breakmins: "",
    dqty: "",
    trips: "",
    location: "",
    load: "",
    loads: "",
    remarks: "",
    volume: "",
    expiry: "",
    uom: "",
  });

  /* ===== Hour UI state (12h + dates) ===== */
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [endDate, setEndDate] = useState("");
  const [startHour12, setStartHour12] = useState("12");
  const [startMinute, setStartMinute] = useState("00");
  const [startAmPm, setStartAmPm] = useState("AM");
  const [endHour12, setEndHour12] = useState("12");
  const [endMinute, setEndMinute] = useState("00");
  const [endAmPm, setEndAmPm] = useState("AM");
  const [endsNextDay, setEndsNextDay] = useState(false);

  useEffect(() => {
    axios
      .get("https://rfs2.infraconsoft.net.in/api/locations")
      .then((r) => setLocations(r.data.locations || []))
      .catch((err) => console.error("Error fetching locations:", err));
    axios.get("https://rfs2.infraconsoft.net.in/api/uom")
  .then((r) => {
    console.log("UOM API Response:", r.data);
    setUom(r.data.uom || r.data || []);
  })
  .catch((err) => console.error("Error fetching uom:", err));

  }, []);
  useEffect(() => {
    if (!branch) return;
    axios
      .get(`https://rfs2.infraconsoft.net.in/api/locations_hire?branch_code=${branch}`)
      .then((r) => setHireLocations(r.data.locations || []))
      .catch((e) => console.error("Error fetching locations:", e));
  }, [branch]);
  useEffect(() => {
    if (id) fetchVehiclePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fallback when old rows don't have start/end dt
  const inferEndDateOffsetDays = (stDec, enDec, wohrs) => {
    const st = Number(stDec ?? 0);
    const en = Number(enDec ?? 0);
    const w = Number(wohrs ?? 0);
    const baseGap = en >= st ? en - st : en - st + 24; // 0..24
    if (!(w > 0)) return en < st ? 1 : 0; // crude fallback
    if (baseGap > w + 0.01) return 0;
    const extra = Math.max(0, w - baseGap);
    const days = Math.floor((extra + 1e-6) / 24);
    return (en < st ? 1 : 0) + days;
  };

  const fetchVehiclePosition = async () => {
    try {
      const { data } = await axios.get(
        `https://rfs2.infraconsoft.net.in/api/vehicle_position_hire/${id}`
      );
      if (!data?.position) return;

      const p = data.position;

      // Prefill base form fields
      setFormData({
        driver: p.fld_drivername || "",
        location: p.fld_location || "",
        sthrs: p.fld_sthrs ?? "",
        endhrs: p.fld_endhrs ?? "",
        breakhrs: p.fld_brehrs ?? "",
        breakmins: p.fld_bremin ?? "",
        stkm: p.fld_stkm ?? "",
        endkm: p.fld_endkm ?? "",
        dqty: p.fld_dieselqty ?? "",
        trips: p.fld_loads ?? "",
        ton: p.fld_tons ?? "",
        load: p.fld_noload ?? "",
        loads: p.fld_noloads ?? "",
        remarks: p.fld_remarks ?? "",
        volume: p.fld_volume ?? "",
        uom: p.fld_uom ?? "",
        expiry: p.fld_driverexpire || "",
      });

      // Hour-basis prefill from new columns if present
      if (String(basis).toLowerCase() === "hour") {
        if (p.fld_start_dt && p.fld_end_dt) {
          const sdt = new Date(p.fld_start_dt);
          const edt = new Date(p.fld_end_dt);

          const sd = ymdLocal(sdt);
          const ed = ymdLocal(edt);

          const sh = sdt.getHours();
          const sm = sdt.getMinutes();
          const eh = edt.getHours();
          const em = edt.getMinutes();

          const s12 = h24To12(sh);
          const e12 = h24To12(eh);

          setStartDate(sd);
          setEndDate(ed);
          setEndsNextDay(ed > sd);
          setStartHour12(s12.h12);
          setStartMinute(pad2(sm));
          setStartAmPm(s12.ampm);
          setEndHour12(e12.h12);
          setEndMinute(pad2(em));
          setEndAmPm(e12.ampm);
        } else {
          // Fallback to decimals + vdate when dt columns are missing
          const vdate = p.fld_vdate ? new Date(p.fld_vdate) : new Date();
          const sd = ymdLocal(vdate);

          const [sh, sm] = decToHM(p.fld_sthrs);
          const [eh, em] = decToHM(p.fld_endhrs);
          const s12 = h24To12(sh);
          const e12 = h24To12(eh);

          const daysOffset = inferEndDateOffsetDays(
            p.fld_sthrs,
            p.fld_endhrs,
            p.fld_wohrs
          );
          const d = new Date(vdate);
          d.setDate(d.getDate() + (daysOffset || 0));
          const ed = ymdLocal(d);

          setStartDate(sd);
          setEndDate(ed);
          setEndsNextDay(daysOffset > 0);
          setStartHour12(s12.h12);
          setStartMinute(pad2(sm));
          setStartAmPm(s12.ampm);
          setEndHour12(e12.h12);
          setEndMinute(pad2(em));
          setEndAmPm(e12.ampm);
        }
      }

      setLoaded(true);
    } catch (error) {
      console.error("Error fetching vehicle position details:", error);
    }
  };

  // Sync end date when toggling next-day
  useEffect(() => {
    if (!loaded) return;
    if (String(basis).toLowerCase() !== "hour") return;
    if (endsNextDay && startDate) {
      const d = new Date(startDate + "T00:00:00");
      d.setDate(d.getDate() + 1);
      setEndDate(ymdLocal(d));
    } else if (!endsNextDay) {
      setEndDate(startDate);
    }
  }, [endsNextDay, startDate, basis, loaded]);

  // Derived hour preview/duration
  const hStart24 = to24h(startHour12, startAmPm);
  const mStart = Number(startMinute);
  const hEnd24 = to24h(endHour12, endAmPm);
  const mEnd = Number(endMinute);
  const startMinutes = startDate
    ? minutesFromDateHM(startDate, hStart24, mStart)
    : NaN;
  const endMinutes = endDate
    ? minutesFromDateHM(endDate, hEnd24, mEnd)
    : NaN;
  const durationMinutes =
    isNaN(startMinutes) || isNaN(endMinutes)
      ? 0
      : Math.max(0, endMinutes - startMinutes);
  const durationHoursDec = +(durationMinutes / 60).toFixed(2);
  const startDec = decFromHM(hStart24, mStart);
  const endDec = decFromHM(hEnd24, mEnd);

  const previewWarnings = useMemo(() => {
    const msgs = [];
    if (
      String(basis).toLowerCase() === "hour" &&
      startDate &&
      endDate &&
      startDate === endDate &&
      endMinutes < startMinutes
    ) {
      msgs.push(
        "End time is earlier than start time on the same day. Did you mean “Ends next day”?"
      );
    }
    if (String(basis).toLowerCase() === "hour" && durationMinutes === 0) {
      msgs.push(
        "Duration is 0 minutes. If this is an overnight (e.g., 12 AM → next-day 12 AM), turn on “Ends next day”."
      );
    }
    return msgs;
  }, [basis, startDate, endDate, startMinutes, endMinutes, durationMinutes]);

  // Handle Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    const cleaned = ["sthrs","endhrs","stkm","endkm","breakhrs","breakmins","dqty","trips","load","loads","volume"]
      .includes(name) ? value.replace(/\s+/g, "") : value;
    setFormData((prev) => ({ ...prev, [name]: cleaned }));
  };

  // Validators
  const isNonNegativeNumber = (v) =>
    v !== "" && !isNaN(Number(v)) && Number(v) >= 0;
  const isNonNegativeInt = (v) => v !== "" && /^\d+$/.test(String(v));

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Common numeric checks
    if (formData.dqty !== "" && !isNonNegativeNumber(formData.dqty)) {
      Swal.fire(
        "Validation Error",
        "Diesel Quantity must be a non-negative number.",
        "warning"
      );
      return;
    }
    if (formData.trips !== "" && !isNonNegativeInt(formData.trips)) {
      Swal.fire(
        "Validation Error",
        "Trips / Output must be a non-negative integer.",
        "warning"
      );
      return;
    }
    if (formData.load !== "" && !isNonNegativeInt(formData.load)) {
      Swal.fire(
        "Validation Error",
        "No of Loads must be a non-negative integer.",
        "warning"
      );
      return;
    }
    if (formData.loads !== "" && !isNonNegativeInt(formData.loads)) {
      Swal.fire(
        "Validation Error",
        "No of Loads must be a non-negative integer.",
        "warning"
      );
      return;
    }
    if (formData.volume !== "" && !isNonNegativeNumber(formData.volume)) {
      Swal.fire(
        "Validation Error",
        "Volume must be a non-negative number (decimals allowed).",
        "warning"
      );
      return;
    }

    // Basis-specific
    let payloadTime = {};
    let kmWorked = 0;

    if (String(basis).toLowerCase() === "hour") {
      if (!startDate || !endDate) {
        Swal.fire(
          "Validation Error",
          "Start and End dates are required.",
          "warning"
        );
        return;
      }
      if (startDate === endDate && endMinutes < startMinutes) {
        Swal.fire(
          "Validation Error",
          "End time is earlier than start time. Enable “Ends next day”.",
          "warning"
        );
        return;
      }
      if (durationMinutes <= 0) {
        Swal.fire(
          "Validation Error",
          "Duration is 0. Check your times or toggle “Ends next day”.",
          "warning"
        );
        return;
      }
      if (durationMinutes > 72 * 60) {
        Swal.fire(
          "Validation Error",
          "Duration exceeds 72 hours. Please verify.",
          "warning"
        );
        return;
      }

const isBlank = (v) =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "");

const isNegativeValue = (v) => {
  if (typeof v === "number") return v < 0;
  const s = String(v).trim();
  if (s === "") return false;
  return s.startsWith("-");
};

const isNonNegativeIntegerValue = (v) => {
  if (typeof v === "number") return Number.isInteger(v) && v >= 0;
  const s = String(v).trim();
  if (!/^\d+$/.test(s)) return false;       // only digits
  const n = Number(s);
  return Number.isInteger(n) && n >= 0;
};

// breaks optional
const hrs = formData.breakhrs;
const mins = formData.breakmins;

// --- Hours ---
if (!isBlank(hrs)) {
  if (isNegativeValue(hrs)) {
    await Swal.fire("Validation Error", "Breaking hours cannot be negative.", "warning");
    document.getElementById("breakhrs")?.focus();
    return;
  }
  if (!isNonNegativeIntegerValue(hrs)) {
    await Swal.fire("Validation Error", "Breaking hours must be a whole number (0, 1, 2, ...).", "warning");
    document.getElementById("breakhrs")?.focus();
    return;
  }
}

// --- Minutes ---
if (!isBlank(mins)) {
  if (isNegativeValue(mins)) {
    await Swal.fire("Validation Error", "Minutes cannot be negative.", "warning");
    document.getElementById("breakmins")?.focus();
    return;
  }
  if (!isNonNegativeIntegerValue(mins)) {
    await Swal.fire("Validation Error", "Minutes must be a whole number (0–59).", "warning");
    document.getElementById("breakmins")?.focus();
    return;
  }
  const minsNum = typeof mins === "number" ? mins : Number(mins);
  if (!Number.isFinite(minsNum) || minsNum >= 60) {
    await Swal.fire("Validation Error", "Minutes must be less than 60.", "warning");
    document.getElementById("breakmins")?.focus();
    return;
  }
}


      payloadTime = {
        sthrs: startDec,
        endhrs: endDec,
        work_hrs: +(durationMinutes / 60).toFixed(2),
        hour_basis: {
          startDate,
          endDate,
          startHour12,
          startMinute,
          startAmPm,
          endHour12,
          endMinute,
          endAmPm,
        },
      };
    } else {
      // KM-type bases
      const startKm = Number(formData.stkm);
      const endKm = Number(formData.endkm);
      if (isNaN(startKm) || isNaN(endKm)) {
        Swal.fire(
          "Validation Error",
          "Starting and Closing KMs are required and must be numbers.",
          "warning"
        );
        return;
      }
      if (startKm < 0 || endKm < 0) {
        Swal.fire(
          "Validation Error",
          "Kilometers cannot be negative.",
          "warning"
        );
        return;
      }
      if (endKm < startKm) {
        Swal.fire(
          "Validation Error",
          "Closing KM cannot be less than Starting KM.",
          "warning"
        );
        return;
      }
      kmWorked = endKm - startKm;
      payloadTime = {
        sthrs: formData.sthrs,
        endhrs: formData.endhrs,
        work_hrs: 0,
      };
    }

    const payload = {
      vcode,
      branch,
      vehname,
      vtype,
      vehno,
      basis,
      stkm: formData.stkm,
      endkm: formData.endkm,
      breakhrs: formData.breakhrs,
      breakmins: formData.breakmins,
      km_worked: kmWorked,
      dqty: formData.dqty,
      trips: formData.trips,
      location: formData.location,
      load: formData.load,
      loads: formData.loads,
      tons: formData.ton,
      remarks: formData.remarks,
      volume: formData.volume,
      driver: formData.driver,
      uom: formData.uom,
      ...(String(basis).toLowerCase() === "hour" && { expiry: formData.expiry }),
      ...payloadTime,
    };

    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await axios.put(
        `https://rfs2.infraconsoft.net.in/api/update_vehicle_position_hire/${id}`,
        payload
      );

      Swal.fire({
        title: "Success!",
        text: "Hire Vehicle position data updated successfully.",
        icon: "success",
      }).then(() => {
        navigate(
          `/vehicle_working_hours_hire/${encodeURIComponent(
            basis
          )}/${encodeURIComponent(vtype)}/${encodeURIComponent(
            vcode
          )}/${encodeURIComponent(vehno)}/${encodeURIComponent(
            vehname
          )}/${encodeURIComponent(branch)}`
        );
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: "Submission failed. Please try again.",
      });
    }
  };

  if (!canEdit) {
    return (
      <div id="admin-container">
        <AdminNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  const startPeriod = toPeriod(formData.sthrs);
  const endPeriod = toPeriod(formData.endhrs);
  const hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => pad2(i));

  return (
    <div id="admin-container">
      <AdminNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Edit Hire Vehicle Position</h2>
            <Link
              to={`/vehicle_working_hours_hire/${encodeURIComponent(
                basis
              )}/${encodeURIComponent(vtype)}/${encodeURIComponent(
                vcode
              )}/${encodeURIComponent(vehno)}/${encodeURIComponent(
                vehname
              )}/${encodeURIComponent(branch)}`}
            >
              <p className="back-button">
                <i className="fa-solid fa-backward"></i> Back
              </p>
            </Link>
          </div>
          <Time />
        </section>

        <div className="form-container">
          <form className="vehicle-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Vehicle Code :</label>
                <input value={vcode} readOnly />
              </div>
              <div className="form-group">
                <label>Branch Name :</label>
                <input value={branch} readOnly />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Vehicle Name :</label>
                <input value={vehname} readOnly />
              </div>
              <div className="form-group">
                <label>Vehicle Number :</label>
                <input value={vehno} readOnly />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Vehicle Basis :</label>
                <input value={basis} readOnly />
              </div>
              <div className="form-group">
                <label>Vehicle Status :</label>
                <input value="Hire" readOnly />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Vehicle Type :</label>
                <input value={vtype} readOnly />
              </div>
              <div className="form-group">
                <label></label>
                <input type="hidden" />
              </div>
            </div>

            <h2 className="center-">Vehicle Position Entry</h2>

            {String(basis).toLowerCase() === "hour" ? (
              <>
                {/* Always show toggle */}
                {/* <div className="form-row">
                  <div className="form-group">
                    <label>Ends next day :</label>
                    <div className="toggle">
                      
                      <label htmlFor="endsNextDay">Ends next day</label>
                    </div>
                  </div>
                  <div className="form-group"><label></label><input type="hidden" /></div>
                </div> */}

                {/* Optional warning banner */}
                {previewWarnings.length > 0 && (
                  <div
                    className="alert alert--warn"
                    role="status"
                    aria-live="polite"
                  >
                    <div className="alert__icon" aria-hidden="true">
                      ⚠️
                    </div>
                    <div className="alert__body">
                      <div className="alert__head">
                        <div className="alert__title">Check your time range</div>

                           <label className="switch">
        <input
                        id="endsNextDay"
                        type="checkbox"
                        checked={endsNextDay}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setEndsNextDay(checked);
                          if (checked && startDate) {
                            const d = new Date(startDate + "T00:00:00");
                            d.setDate(d.getDate() + 1);
                            setEndDate(ymdLocal(d));
                          } else if (!checked) {
                            setEndDate(startDate);
                          }
                        }}
                      />
          <span className="switch__track" aria-hidden="true"></span>
          <span className="switch__label">Ends next day</span>
        </label>
                      </div>
                      <div className="alert__msg">
                        {previewWarnings.map((w, i) => (
                          <p key={i} className="warn">
                            {w}
                          </p>
                        ))}
                        <div className="alert__hint">
                          Overnight example: <b>12:00 AM → next-day 12:00 AM</b>.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <br />

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date :</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setStartDate(val);
                        // keep toggle effect coherent
                        if (endsNextDay && val) {
                          const d = new Date(val + "T00:00:00");
                          d.setDate(d.getDate() + 1);
                          setEndDate(ymdLocal(d));
                        } else if (!endsNextDay) {
                          setEndDate(val);
                        }
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>End Date :</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEndDate(val);
                        if (startDate) setEndsNextDay(val > startDate);
                      }}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Time :</label>
                    <div className="time-group field-control">
                      <select
                        value={startHour12}
                        onChange={(e) => setStartHour12(e.target.value)}
                      >
                        {hourOptions.map((h) => (
                          <option key={h}>{h}</option>
                        ))}
                      </select>
                      <span>:</span>
                      <select
                        value={startMinute}
                        onChange={(e) => setStartMinute(e.target.value)}
                      >
                        {minuteOptions.map((m) => (
                          <option key={m}>{m}</option>
                        ))}
                      </select>
                      <select
                        value={startAmPm}
                        onChange={(e) => setStartAmPm(e.target.value)}
                      >
                        <option>AM</option>
                        <option>PM</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>End Time :</label>
                    <div className="time-group field-control">
                      <select
                        value={endHour12}
                        onChange={(e) => setEndHour12(e.target.value)}
                      >
                        {hourOptions.map((h) => (
                          <option key={h}>{h}</option>
                        ))}
                      </select>
                      <span>:</span>
                      <select
                        value={endMinute}
                        onChange={(e) => setEndMinute(e.target.value)}
                      >
                        {minuteOptions.map((m) => (
                          <option key={m}>{m}</option>
                        ))}
                      </select>
                      <select
                        value={endAmPm}
                        onChange={(e) => setEndAmPm(e.target.value)}
                      >
                        <option>AM</option>
                        <option>PM</option>
                      </select>
                    </div>
                  </div>
                </div>

            </>
            ) : (
              <>
              <div className="form-row">
                <div className="form-group">
                  <label>Starting km :</label>
                  <input
                    name="stkm"
                    value={formData.stkm}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Closing km :</label>
                  <input
                    name="endkm"
                    value={formData.endkm}
                    onChange={handleChange}
                  />
                </div>
              </div>

           <div className="form-row">
  <div className="form-group">
    <label htmlFor="breakhrs">Breaking Hours :</label>
    <input
      id="breakhrs"
      name="breakhrs"
      inputMode="numeric"
      type="number"
      min="0"
      step="1"
      value={formData.breakhrs ?? ""}
      onChange={handleChange}
      onKeyDown={(e) => {
        // block minus, plus, dot, exponent
        if (["-", "+", ".", "e", "E"].includes(e.key)) e.preventDefault();
      }}
      onWheel={(e) => e.currentTarget.blur()} // avoid accidental scroll change
    />
  </div>

  <div className="form-group">
    <label htmlFor="breakmins">Minutes :</label>
    <input
      id="breakmins"
      name="breakmins"
      inputMode="numeric"
      type="number"
      min="0"
      max="59"
      step="1"
      value={formData.breakmins ?? ""}
      onChange={handleChange}
      onKeyDown={(e) => {
        if (["-", "+", ".", "e", "E"].includes(e.key)) e.preventDefault();
      }}
      onWheel={(e) => e.currentTarget.blur()}
    />
  </div>
</div>

</>
            )}

            {/* Breaks */}
            
            {String(basis).toLowerCase() === "hour" && (
              <div className="form-row">
                <div className="form-group">
                  <label>Driver Name :</label>
                  <input
                    name="driver"
                    value={formData.driver}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Driver Licence Expire Date :</label>
                  <input
                    type="date"
                    name="expiry"
                    value={
                      formData.expiry
                        ? ymdLocal(new Date(formData.expiry))
                        : ""
                    }
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Diesel Quantity :</label>
                <input
                  name="dqty"
                  value={formData.dqty}
                  onChange={handleChange}
                />
              </div>
<div className="form-group">
  <label>
    {String(basis).toLowerCase() === "ton" ? "No. of Tons :" : "No. of Trips / Output :"}
  </label>
  <input
    name={String(basis).toLowerCase() === "ton" ? "ton" : "trips"}
    value={String(basis).toLowerCase() === "ton" ? formData.ton : formData.trips}
    onChange={handleChange}
  />
</div>

            </div>

            <div className="form-row">
              {String(basis).toLowerCase() === "hour" ? (
                <div className="form-group">
                  <label>Location :</label>
                  <select
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                  >
                    <option value="">-- Select Location --</option>
                    {locations.map((l, i) => (
                      <option key={i} value={l.fld_location}>
                        {l.fld_location}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>Location :</label>
                  <select
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                  >
                    <option value="">-- Select Location --</option>
                    {hireLocations.map((l, i) => (
                      <option key={i} value={l.fld_location}>
                        {l.fld_location}
                      </option>
                    ))}
                  </select>
                </div>
              )}


              {["ton", "month"].includes(String(basis).toLowerCase()) ? (
  <div className="form-group">
    <label>No of Load :</label>
    <input
      type="number"
      name="loads"
      value={formData.loads}
      onChange={handleChange}
    />
  </div>
) : (
  <div className="form-group">
    <label>No of Loads :</label>
    <input
      type="number"
      name="load"
      value={formData.load}
      onChange={handleChange}
    />
  </div>
)}

            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Remarks :</label>
                <input
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Volume :</label>
                <input
                  name="volume"
                  value={formData.volume}
                  onChange={handleChange}
                />
              </div>
            </div>

            {["ton", "month"].includes(String(basis).toLowerCase()) && (
  <div className="form-row">
    <div className="form-group">
      <label>UOM :</label>
      <select
        name="uom"
        value={formData.uom}
        onChange={handleChange}
      >
        {uom.map((item, index) => (
          <option key={index} value={item.uommaster_shortname}>
            {item.uommaster_shortname}
          </option>
        ))}
      </select>
    </div>
    <div className="form-group">
      <label></label>
      <input type="hidden" />
    </div>
  </div>
)}


            <div className="btn-cls">
              <button type="submit" className="submit-btn">
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Vehicle_position_edit_hire;
